import { useRef, useState, useEffect } from "react";
import {
  Move, Maximize2, Plus, Grid3x3,
  AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  Copy, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// A4 dimensions
const A4_W_MM = 210;
const A4_H_MM = 297;
const SCALE = 2.6; // px per mm
const CANVAS_W = A4_W_MM * SCALE;
const CANVAS_H = A4_H_MM * SCALE;
const SAFE_TOP_MM = 60; // header zone (auto-rendered)
const SAFE_BOTTOM_MM = 25; // footer zone (auto-rendered)

const FIELD_TYPE_LABELS = {
  text: "نص قصير", textarea: "نص طويل", number: "رقم", email: "بريد",
  phone: "هاتف", date: "تاريخ", select: "قائمة", checkbox: "خانات",
  radio: "اختيار", file: "ملف/صورة", signature: "توقيع",
};

function clamp(p, min, max) {
  return Math.max(min, Math.min(p, max));
}

function snapTo(value, gridSize) {
  return Math.round(value / gridSize) * gridSize;
}

export default function PdfLayoutDesigner({ fields, layout, onChange }) {
  const canvasRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const dragRef = useRef(null);
  const [snap, setSnap] = useState(true);
  const gridSize = 5; // mm

  const positions = layout?.positions || [];
  const positioned = new Set(positions.map((p) => p.field_id));
  const unplaced = fields.filter((f) => !positioned.has(f.id));

  const updatePos = (id, patch) => {
    const next = positions.map((p) => (p.field_id === id ? { ...p, ...patch } : p));
    onChange({ ...layout, positions: next });
  };

  const addPlacement = (field) => {
    // Find next free Y position below safe top zone
    const usedY = positions.map((p) => p.y + p.height);
    const baseY = positions.length ? Math.max(SAFE_TOP_MM, ...usedY) + 4 : SAFE_TOP_MM + 5;
    const isWide = ["textarea", "signature", "file"].includes(field.field_type);
    const newPos = {
      field_id: field.id,
      x: 15,
      y: clamp(baseY, SAFE_TOP_MM, A4_H_MM - SAFE_BOTTOM_MM - 18),
      width: isWide ? 180 : 90,
      height: ["textarea", "signature"].includes(field.field_type) ? 28 : 14,
      show_label: true,
      font_size: 10,
    };
    onChange({ ...layout, positions: [...positions, newPos] });
    setSelected(field.id);
  };

  const removePlacement = (id) => {
    onChange({ ...layout, positions: positions.filter((p) => p.field_id !== id) });
    if (selected === id) setSelected(null);
  };

  const duplicatePlacement = (id) => {
    const p = positions.find((x) => x.field_id === id);
    if (!p) return;
    // duplicating same field doesn't make sense (each field appears once on PDF).
    // Instead just copy properties to next un-placed field of same type? Skip — disabled.
    return p;
  };

  const alignField = (mode) => {
    if (!selected) return;
    const p = positions.find((x) => x.field_id === selected);
    if (!p) return;
    let x = p.x;
    if (mode === "left") x = 15;
    else if (mode === "center") x = (A4_W_MM - p.width) / 2;
    else if (mode === "right") x = A4_W_MM - p.width - 15;
    updatePos(selected, { x: snap ? snapTo(x, gridSize) : x });
  };

  // Mouse-based drag/resize. Using LTR coordinate space throughout (canvas direction:ltr).
  const onMouseDownDrag = (e, fieldId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(fieldId);
    const pos = positions.find((p) => p.field_id === fieldId);
    if (!pos) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = pos.x;
    const origY = pos.y;
    dragRef.current = { mode: "move", fieldId, startX, startY, origX, origY };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dxMm = (ev.clientX - d.startX) / SCALE;
      const dyMm = (ev.clientY - d.startY) / SCALE;
      const cur = positions.find((p) => p.field_id === d.fieldId);
      let newX = d.origX + dxMm;
      let newY = d.origY + dyMm;
      if (snap) {
        newX = snapTo(newX, gridSize);
        newY = snapTo(newY, gridSize);
      }
      updatePos(d.fieldId, {
        x: clamp(newX, 0, A4_W_MM - cur.width),
        y: clamp(newY, SAFE_TOP_MM, A4_H_MM - SAFE_BOTTOM_MM - cur.height),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onMouseDownResize = (e, fieldId) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = positions.find((p) => p.field_id === fieldId);
    if (!pos) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origW = pos.width;
    const origH = pos.height;
    dragRef.current = { mode: "resize", fieldId, startX, startY, origW, origH };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dwMm = (ev.clientX - d.startX) / SCALE;
      const dhMm = (ev.clientY - d.startY) / SCALE;
      const cur = positions.find((p) => p.field_id === d.fieldId);
      let newW = d.origW + dwMm;
      let newH = d.origH + dhMm;
      if (snap) {
        newW = snapTo(newW, gridSize);
        newH = snapTo(newH, gridSize);
      }
      updatePos(d.fieldId, {
        width: clamp(newW, 20, A4_W_MM - cur.x),
        height: clamp(newH, 8, A4_H_MM - SAFE_BOTTOM_MM - cur.y),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Keyboard delete + arrow nudge
  useEffect(() => {
    const onKey = (e) => {
      if (!selected) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removePlacement(selected);
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      const cur = positions.find((p) => p.field_id === selected);
      if (!cur) return;
      const update = {};
      if (e.key === "ArrowLeft") update.x = clamp(cur.x - step, 0, A4_W_MM - cur.width);
      if (e.key === "ArrowRight") update.x = clamp(cur.x + step, 0, A4_W_MM - cur.width);
      if (e.key === "ArrowUp") update.y = clamp(cur.y - step, SAFE_TOP_MM, A4_H_MM - SAFE_BOTTOM_MM - cur.height);
      if (e.key === "ArrowDown") update.y = clamp(cur.y + step, SAFE_TOP_MM, A4_H_MM - SAFE_BOTTOM_MM - cur.height);
      if (Object.keys(update).length) {
        e.preventDefault();
        updatePos(selected, update);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [selected, positions]);

  const selectedPos = positions.find((p) => p.field_id === selected);
  const selectedField = fields.find((f) => f.id === selected);

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="pdf-layout-designer">
      {/* LEFT: Unplaced fields list */}
      <div className="col-span-12 lg:col-span-3 space-y-4">
        <div className="gov-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
            الحقول المتاحة
          </div>
          <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
            {unplaced.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-6">
                تم وضع جميع الحقول
              </div>
            )}
            {unplaced.map((f) => (
              <button
                key={f.id}
                onClick={() => addPlacement(f)}
                data-testid={`pdf-add-field-${f.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-sm border border-border bg-card hover:border-primary hover:bg-accent transition-all text-sm text-start"
              >
                <Plus className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="flex-1 truncate">{f.label}</span>
                <span className="text-xs text-muted-foreground shrink-0">{FIELD_TYPE_LABELS[f.field_type] || f.field_type}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Toolbar */}
        <div className="gov-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
            أدوات التصميم
          </div>
          <div className="p-3 space-y-3">
            <label className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5"><Grid3x3 className="w-3.5 h-3.5" /> الانطباق على الشبكة</span>
              <Switch checked={snap} onCheckedChange={setSnap} data-testid="snap-toggle" />
            </label>
            <div>
              <div className="text-xs text-muted-foreground mb-2">محاذاة الحقل المحدد:</div>
              <div className="grid grid-cols-3 gap-1">
                <Button variant="outline" size="sm" disabled={!selected} onClick={() => alignField("left")} data-testid="align-left" title="إلى اليسار">
                  <AlignStartHorizontal className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={!selected} onClick={() => alignField("center")} data-testid="align-center" title="توسيط">
                  <AlignCenterHorizontal className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={!selected} onClick={() => alignField("right")} data-testid="align-right" title="إلى اليمين">
                  <AlignEndHorizontal className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">
              <div className="font-medium mb-1">اختصارات:</div>
              <ul className="space-y-0.5 list-disc ps-4">
                <li>أسهم لوحة المفاتيح: تحريك دقيق</li>
                <li>Shift + سهم: قفزة 10 مم</li>
                <li>Backspace: حذف المحدد</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CENTER: A4 Canvas */}
      <div className="col-span-12 lg:col-span-6">
        <div className="text-xs text-muted-foreground mb-2 font-mono flex items-center justify-between">
          <span>A4 — {A4_W_MM} × {A4_H_MM} mm</span>
          <span>{positions.length} حقل / {fields.length}</span>
        </div>
        <div className="relative inline-block bg-white border border-border shadow-sm" style={{ direction: "ltr" }}>
          <div
            ref={canvasRef}
            onClick={() => setSelected(null)}
            className="relative bg-white"
            style={{
              width: CANVAS_W,
              height: CANVAS_H,
              backgroundImage:
                "linear-gradient(to right, hsl(var(--border) / 0.4) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border) / 0.4) 1px, transparent 1px)",
              backgroundSize: `${gridSize * SCALE}px ${gridSize * SCALE}px`,
            }}
          >
            {/* Top safe area (header) */}
            <div
              className="absolute inset-x-0 top-0 bg-primary/[0.04] border-b border-dashed border-primary/30 pointer-events-none flex items-center justify-center"
              style={{ height: SAFE_TOP_MM * SCALE, direction: "rtl" }}
            >
              <div className="text-[10px] text-primary/60 px-3 text-center font-medium">
                ⓘ منطقة الترويسة (الشعار + العنوان + رقم الاستمارة + QR) — تُرسم تلقائياً
              </div>
            </div>

            {/* Bottom safe area (footer) */}
            <div
              className="absolute inset-x-0 bottom-0 bg-primary/[0.04] border-t border-dashed border-primary/30 pointer-events-none flex items-center justify-center"
              style={{ height: SAFE_BOTTOM_MM * SCALE, direction: "rtl" }}
            >
              <div className="text-[10px] text-primary/60 px-3 text-center font-medium">
                ⓘ منطقة التذييل + التوقيع — تُرسم تلقائياً
              </div>
            </div>

            {positions.map((p) => {
              const f = fields.find((x) => x.id === p.field_id);
              if (!f) return null;
              const isSel = selected === p.field_id;
              return (
                <div
                  key={p.field_id}
                  onMouseDown={(e) => onMouseDownDrag(e, p.field_id)}
                  onClick={(e) => { e.stopPropagation(); setSelected(p.field_id); }}
                  data-testid={`pdf-pos-${p.field_id}`}
                  className={`absolute select-none cursor-move ${isSel ? "ring-2 ring-primary z-10 shadow-md" : "ring-1 ring-border hover:ring-primary/50"} bg-white/95`}
                  style={{
                    left: p.x * SCALE,
                    top: p.y * SCALE,
                    width: p.width * SCALE,
                    height: p.height * SCALE,
                    direction: "rtl",
                  }}
                >
                  <div className="p-1.5 h-full overflow-hidden pointer-events-none">
                    {p.show_label && (
                      <div className="text-[9px] font-medium text-muted-foreground leading-tight truncate">
                        {f.label}
                      </div>
                    )}
                    <div className="text-[9px] text-foreground/50 leading-tight font-mono">
                      [{FIELD_TYPE_LABELS[f.field_type] || f.field_type}]
                    </div>
                  </div>
                  {isSel && (
                    <>
                      <Move className="absolute -top-3 left-1 w-4 h-4 bg-primary text-primary-foreground rounded-sm p-0.5" />
                      <button
                        type="button"
                        onMouseDown={(e) => onMouseDownResize(e, p.field_id)}
                        className="absolute -bottom-2 -right-2 w-4 h-4 bg-primary cursor-nwse-resize rounded-sm flex items-center justify-center hover:bg-primary/90"
                        title="تغيير الحجم"
                      >
                        <Maximize2 className="w-3 h-3 text-primary-foreground" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* RIGHT: Properties / general */}
      <div className="col-span-12 lg:col-span-3 space-y-4">
        <div className="gov-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
            إعدادات عامة
          </div>
          <div className="p-4 space-y-3">
            <label className="flex items-center justify-between text-sm">
              <span>عرض رقم الاستمارة</span>
              <Switch checked={layout.show_form_number !== false} onCheckedChange={(c) => onChange({ ...layout, show_form_number: c })} />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span>عرض QR Code</span>
              <Switch checked={layout.show_qr !== false} onCheckedChange={(c) => onChange({ ...layout, show_qr: c })} />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span>عرض التذييل</span>
              <Switch checked={layout.show_footer !== false} onCheckedChange={(c) => onChange({ ...layout, show_footer: c })} />
            </label>
          </div>
        </div>

        {selectedPos && selectedField ? (
          <div className="gov-card overflow-hidden" data-testid="pdf-field-properties">
            <div className="px-4 py-3 border-b border-border bg-surface flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium truncate">
                {selectedField.label}
              </div>
              <button onClick={() => removePlacement(selected)} className="text-destructive p-1 rounded hover:bg-destructive/10" title="حذف">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">X (مم)</Label>
                  <Input type="number" value={Math.round(selectedPos.x)} onChange={(e) => updatePos(selected, { x: clamp(+e.target.value, 0, A4_W_MM - selectedPos.width) })} />
                </div>
                <div>
                  <Label className="text-xs">Y (مم)</Label>
                  <Input type="number" value={Math.round(selectedPos.y)} onChange={(e) => updatePos(selected, { y: clamp(+e.target.value, SAFE_TOP_MM, A4_H_MM - SAFE_BOTTOM_MM - selectedPos.height) })} />
                </div>
                <div>
                  <Label className="text-xs">العرض (مم)</Label>
                  <Input type="number" value={Math.round(selectedPos.width)} onChange={(e) => updatePos(selected, { width: clamp(+e.target.value, 20, A4_W_MM - selectedPos.x) })} />
                </div>
                <div>
                  <Label className="text-xs">الارتفاع (مم)</Label>
                  <Input type="number" value={Math.round(selectedPos.height)} onChange={(e) => updatePos(selected, { height: clamp(+e.target.value, 8, A4_H_MM - SAFE_BOTTOM_MM - selectedPos.y) })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">حجم الخط (نقطة)</Label>
                <Input type="number" value={selectedPos.font_size || 10} onChange={(e) => updatePos(selected, { font_size: clamp(+e.target.value, 7, 24) })} />
              </div>
              <label className="flex items-center justify-between">
                <span>عرض عنوان الحقل</span>
                <Switch checked={selectedPos.show_label !== false} onCheckedChange={(c) => updatePos(selected, { show_label: c })} />
              </label>
            </div>
          </div>
        ) : (
          <div className="gov-card p-6 text-center text-sm text-muted-foreground">
            اختر حقلاً على الصفحة لتعديل خصائصه
          </div>
        )}
      </div>
    </div>
  );
}
